import { all, put, select, takeEvery, takeLeading } from 'redux-saga/effects'

import ApiSagas from '../api/sagas'
import { services } from '../../graphql'
import { actions, selectors } from '..'
import { User } from '../../graphql/generated/graphql'
import { declarationToFullDeclaration } from '../../helpers/DeclarationHelpers'
import { Child } from '../../types'

export default class DeclarationSagas {
  static *onInit() {
    yield
  }

  static *onInitFormValues() {
    const user: User = yield select(selectors.auth.user)

    // Reset form values if still stored within local storage
    yield put(actions.declaration.resetFormValues())

    // Set terms
    yield put(actions.declaration.setFormValues({ name: 'terms', value: !!user.cgu_accepted_at }))

    // Set CCI Function
    yield put(
      actions.declaration.setFormValues({
        name: 'cciFunction',
        value: user.declaration.function?.id ?? '',
      })
    )

    // Set Intent
    yield put(
      actions.declaration.setFormValues({
        name: 'intent',
        value: user.id ?? '',
      })
    )

    // Set declaration items
    if (user?.declaration?.items?.length) {
      yield all(
        user.declaration.items.map((item) => {
          return put(
            actions.declaration.appendNewDeclarationItem(declarationToFullDeclaration(item))
          )
        })
      )
    }

    // Set children
    if (user?.declaration?.children?.length) {
      yield all(
        user.declaration.children.map((child, idx) => {
          return put(
            actions.declaration.setChild({
              child: {
                civility: child.civility,
                firstName: child.firstName,
                lastName: child.lastName,
                familyRelationshipId: child.familyRelationship?.id ?? null,
                familyRelationshipCustom: child.familyRelationshipCustom ?? null,
                items: child?.items?.map((item) => declarationToFullDeclaration(item)),
              },
              index: idx,
            })
          )
        })
      )

      yield all(
        user.declaration.children.map((child, idx) => {
          return child?.items?.map((item) => {
            return put(
              actions.declaration.appendChildDeclarationItem({
                item: declarationToFullDeclaration(item),
                childIndex: idx,
              })
            )
          })
        })
      )
    }

    // Set Partner
    if (user?.declaration?.partner) {
      yield put(
        actions.declaration.setPartner({
          civility: user.declaration.partner.civility,
          firstName: user.declaration.partner.firstName,
          lastName: user.declaration.partner.lastName,
        })
      )

      if (user?.declaration.partner.items?.length) {
        yield all(
          user.declaration.partner.items.map((item) => {
            return put(
              actions.declaration.appendPartnerDeclarationItem(declarationToFullDeclaration(item))
            )
          })
        )
      }
    }
  }

  static *onFunctionsRequest({ payload }: ReturnType<typeof actions.declaration.functionsRequest>) {
    const rs = yield* ApiSagas.call(services.declaration.queries.functions, {
      first: payload.first,
    })

    if (rs.errors) {
      yield put(actions.declaration.functionsError(rs.errors))
    }

    if (rs.data) {
      yield put(actions.declaration.setDeclarationFunctions(rs.data.functions.data))
    }
  }

  static *onFamilyRelationshipsRequest() {
    const rs = yield* ApiSagas.call(services.declaration.queries.familyRelationships, {})

    if (rs.errors) {
      yield put(actions.declaration.familyRelationshipsError(rs.errors))
    }

    if (rs.data) {
      yield put(actions.declaration.setFamilyRelationships(rs.data.familyRelationships))
    }
  }

  static *onSaveDeclaration() {
    const formValues: ReturnType<typeof selectors.declaration.formValues> = yield select(
      selectors.declaration.formValues
    )

    const payload = {
      function: formValues.cciFunction,
      children: null,
      partner: null,
      ...(formValues?.children &&
        formValues.children.length > 0 && {
          children: formValues.children.map((child: Child) => ({
            civility: child.civility,
            firstName: child.firstName,
            lastName: child.lastName,
            familyRelationshipId: child.familyRelationshipId ?? null,
            familyRelationshipCustom: child.familyRelationshipCustom ?? null,
            items: child?.items?.map((item) => ({
              function: item.companyJob,
              type: item.type,
              establishment: {
                siren: item.siren,
                entity: item.entity,
                address: item.address,
                city: item.city,
                postalCode: item.postalCode,
              },
            })),
          })),
        }),
      ...(formValues?.partner?.items &&
        formValues.partner.items.length > 0 && {
          partner: {
            civility: formValues.partner.civility,
            firstName: formValues.partner.firstName,
            lastName: formValues.partner.lastName,
            items: formValues.partner.items.map((item: any) => ({
              function: item.companyJob,
              type: item.type,
              establishment: {
                siren: item.siren,
                entity: item.entity,
                address: item.address,
                city: item.city,
                postalCode: item.postalCode,
              },
            })),
          },
        }),
      items: formValues.items.map((item: any) => ({
        function: item.companyJob,
        type: item.type,
        establishment: {
          siren: item.siren,
          entity: item.entity,
          address: item.address,
          city: item.city,
          postalCode: item.postalCode,
        },
      })),
    }

    const rs = yield* ApiSagas.call(services.declaration.mutations.saveDeclaration, {
      function: payload.function,
      items: payload.items,
      partner: payload.partner,
      children: payload.children,
    })

    if (rs.errors) {
      yield put(actions.declaration.saveDeclarationError(rs.errors))
    }

    if (rs.data) {
      yield put(actions.declaration.saveDeclarationSuccess(rs.data))

      // Reload user once declaration is saved
      yield put(actions.auth.setUser(rs.data.user))
    }
  }

  static *onAcceptCgu() {
    const rs = yield* ApiSagas.call(services.declaration.mutations.acceptCgu)

    if (!rs.errors && rs.data) {
      yield put(actions.auth.setUser(rs.data))
      yield put(actions.declaration.acceptCguSuccess(rs.data))
    } else {
      yield put(actions.declaration.acceptCguError(rs.errors))
    }
  }

  static *onValidateSirenRequest({
    payload,
  }: ReturnType<typeof actions.declaration.validateSirenRequest>) {
    const rs = yield* ApiSagas.call(services.declaration.queries.validateSiren, payload)

    if (!rs.errors && rs.data) {
      yield put(actions.declaration.validateSirenSuccess(rs.data))
    } else {
      yield put(actions.declaration.validateSirenError(rs.errors))
    }
  }

  static *onValidateSirenOrRnaRequest({
    payload,
  }: ReturnType<typeof actions.declaration.validateSirenOrRnaRequest>) {
    const rs = yield* ApiSagas.call(services.declaration.queries.validateSirenOrRna, payload)

    if (!rs.errors && rs.data) {
      yield put(actions.declaration.validateSirenOrRnaSuccess(rs.data))
    } else {
      yield put(actions.declaration.validateSirenOrRnaError(rs.errors))
    }
  }

  static *listeners() {
    yield all([
      takeLeading(actions.declaration.initFormValues, this.onInitFormValues),
      takeEvery(actions.declaration.functionsRequest, this.onFunctionsRequest),
      takeEvery(actions.declaration.familyRelationshipsRequest, this.onFamilyRelationshipsRequest),
      takeLeading(actions.declaration.saveDeclarationRequest, this.onSaveDeclaration),
      takeLeading(actions.declaration.acceptCguRequest, this.onAcceptCgu),
      takeLeading(actions.declaration.validateSirenRequest, this.onValidateSirenRequest),
      takeLeading(actions.declaration.validateSirenOrRnaRequest, this.onValidateSirenOrRnaRequest),
    ])
  }
}
